export type LinkStatus = "not_checked" | "online" | "degraded" | "offline";
export type CheckBackend = "cloudflare_tcp" | "local_agent_tcp";

export interface LinkView {
  id: string;
  name: string;
  originalUrl: string;
  shortCode: string;
  enabled: boolean;
  checkBackend: CheckBackend;
  lastStatus: LinkStatus;
  lastCheckedAt?: string;
  lastCheckRunId?: string;
  lastCheckedCount?: number;
  lastOnlineCount?: number;
  lastFailedCount?: number;
  lastSkippedCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface LinksResponse {
  links: LinkView[];
}

export interface SessionResponse {
  authenticated: boolean;
}

export interface LoginResponse {
  ok: true;
}

export interface RunAllResponse {
  runIds: string[];
  runs: Array<{
    linkId: string;
    runId: string;
  }>;
  failures?: Array<{
    linkId: string;
    error: string;
  }>;
}

export interface LinkInput {
  name: string;
  originalUrl: string;
  enabled: boolean;
  checkBackend?: CheckBackend;
}

export interface LinkResponse {
  link: LinkView;
}

export interface RunLinkResponse {
  runId: string;
}

export interface DeleteResponse {
  ok: true;
}

export interface SettingsView {
  dailyCheckTime: string;
  timezone: "Asia/Shanghai";
  checkTimeoutMs: number;
  telegramChatId: string;
  publicSubWorkerOrigin: string;
}

export type SettingsInput = Omit<SettingsView, "timezone">;

export interface SettingsResponse {
  settings: SettingsView;
}

export interface TestTelegramResponse {
  ok: true;
}

export class ApiError extends Error {
  readonly status: number;
  readonly details: unknown;

  constructor(message: string, status: number, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.details = details;
  }
}

type JsonBody = object;
type ApiInit = Omit<RequestInit, "body"> & {
  body?: BodyInit | JsonBody;
};

export async function api<T>(path: string, init: ApiInit = {}): Promise<T> {
  const { body: rawBody, headers: rawHeaders, ...requestInit } = init;
  const headers = new Headers(rawHeaders);
  headers.set("accept", headers.get("accept") ?? "application/json");

  let body = rawBody as BodyInit | null | undefined;
  if (isJsonSerializableBody(rawBody)) {
    body = JSON.stringify(rawBody);
    headers.set("content-type", headers.get("content-type") ?? "application/json");
  }

  const response = await fetch(path, {
    ...requestInit,
    body,
    credentials: requestInit.credentials ?? "same-origin",
    headers
  });
  const payload = await readJsonSafely(response);

  if (!response.ok) {
    throw new ApiError(readErrorMessage(payload, response.status), response.status, payload);
  }

  return payload as T;
}

export function createLink(input: LinkInput): Promise<LinkResponse> {
  return api<LinkResponse>("/api/links", {
    method: "POST",
    body: input
  });
}

export function updateLink(id: string, input: LinkInput): Promise<LinkResponse> {
  return api<LinkResponse>(`/api/links/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: input
  });
}

export function deleteLink(id: string): Promise<DeleteResponse> {
  return api<DeleteResponse>(`/api/links/${encodeURIComponent(id)}`, {
    method: "DELETE"
  });
}

export function runLinkCheck(id: string): Promise<RunLinkResponse> {
  return api<RunLinkResponse>(`/api/links/${encodeURIComponent(id)}/run`, {
    method: "POST"
  });
}

export function getSettings(): Promise<SettingsResponse> {
  return api<SettingsResponse>("/api/settings");
}

export function updateSettings(input: SettingsInput): Promise<SettingsResponse> {
  return api<SettingsResponse>("/api/settings", {
    method: "PUT",
    body: input
  });
}

export function testTelegramSettings(): Promise<TestTelegramResponse> {
  return api<TestTelegramResponse>("/api/settings/test-telegram", {
    method: "POST"
  });
}

function isJsonSerializableBody(value: ApiInit["body"]): value is JsonBody {
  if (value === undefined || value === null || typeof value !== "object") return false;
  if (typeof FormData !== "undefined" && value instanceof FormData) return false;
  if (typeof URLSearchParams !== "undefined" && value instanceof URLSearchParams) return false;
  if (typeof Blob !== "undefined" && value instanceof Blob) return false;
  if (typeof ReadableStream !== "undefined" && value instanceof ReadableStream) return false;
  if (value instanceof ArrayBuffer || ArrayBuffer.isView(value)) return false;
  return true;
}

async function readJsonSafely(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return undefined;

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}

function readErrorMessage(payload: unknown, status: number): string {
  if (isRecord(payload) && typeof payload.error === "string" && payload.error.trim()) {
    return payload.error;
  }
  return `Request failed (${status})`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
